// Single source of truth for classifying a product-gallery media item and the
// thumbnail glyph for non-image/video kinds. Shared by the site editor's gallery pane,
// the storefront gallery, and the fullscreen lightbox so
// every surface classifies + labels gallery media identically. Leaf module (only
// depends on fileUtils) to avoid an import cycle with the store barrel, which
// re-exports from ./StoreParts.
import { getPreviewKind, type PreviewKind } from '@/utils/fileUtils';

export type GalleryMediaItem = {
  src: string;
  altText: string | null;
  mime?: string;
  filename?: string;
  poster?: string | null;
  /** Signed CDN URL for a ready ~24px plaintext blur-up placeholder of `src`,
   *  when one exists. Always free-CDN-servable — deliberately NOT the
   *  classic 96px Library thumb, which is always encrypted on B2 (even for
   *  published media) and can't be redirected to the CDN without a
   *  droplet-side decrypt first. Same absent-is-fine contract as variantSrc. */
  variantPlaceholderSrc?: string | null;
  /** Signed CDN URL for a ready ~960px lightweight variant of `src`, when one
   *  exists (server/mediaCdn.js attachSiteMediaVariants → buildMergedGallery
   *  carries it through). Absent = no variant yet / CDN off — callers fall
   *  back to `src` itself, identical to today. */
  variantSrc?: string | null;
  /** Signed CDN URL for a ready ~1600px large tier of `src`, when one exists
   *  — requested only after every other small-tier image on the page has
   *  settled (useProgressiveTierSrc). Same absent-is-fine contract. */
  variantLgSrc?: string | null;
  /** Same idea for `poster` (video items only) — a larger, sharper generated
   *  poster frame than the classic 96px Library grid thumb. */
  posterVariantSrc?: string | null;
  /** Re-encoded low-bitrate MP4 variant of `src` (video items only), when
   *  ready. Same absent-is-fine contract as variantSrc. */
  videoVariantSrc?: string | null;
};

// Classify a gallery item. Defaults to 'image' when mime/filename are absent
// (raw Shopify product images, which otherwise carry an 'image/' mime sentinel).
export function galleryMediaKind(item: { mime?: string; filename?: string }): PreviewKind {
  return (item.mime || item.filename) ? getPreviewKind(item.mime || '', item.filename || '') : 'image';
}

// Glyph thumbnail for a gallery kind that isn't a previewable image/video.
export const GALLERY_KIND_GLYPH: Partial<Record<PreviewKind, string>> = {
  model: '🧊', pdf: '📄', audio: '🎵', presentation: '📽️', shader: '🌈', other: '📁',
};
